import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { translateBatch } from '../utils/translate';
import { restrictedWords } from '../utils/translationConfig';
import { getOverride } from '../utils/translationOverrides';
import { transliterate } from '../utils/transliterate';

// ============================================================================
//  Whole-DOM runtime translation
// ----------------------------------------------------------------------------
//  Goals: fast (cached, incremental, batched) and accurate (domain overrides,
//  attribute translation, no mangling of numbers / URLs / inputs).
//
//  Strategy:
//   1. Maintain an in-memory WeakMap of node -> original text (source of truth)
//      and node -> last applied lang (prevents redundant work on re-renders).
//   2. MutationObserver fires incrementally — we only translate the NEW nodes,
//      not the whole body. Mutations are coalesced in a 120ms window.
//   3. A persistent cache (`hash(text)|lang -> translated`) lives in
//      localStorage, so the second page visit is instant and the second
//      session has zero Google calls for known text.
//   4. Attributes (placeholder, title, aria-label, alt) are translated the
//      same way text nodes are.
//   5. Tokens protect restricted domain terms before hitting Google and are
//      restored afterwards, optionally via transliteration for Indic langs.
// ============================================================================

// Elements whose ENTIRE subtree (including attributes) is ignored.
const SKIP_ENTIRELY = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT']);
// Elements whose text children are ignored (they hold user-authored data
// or raw code) but whose translatable attributes MUST still be translated —
// e.g. <input placeholder="Search...">.
const SKIP_TEXT_KEEP_ATTRS = new Set(['INPUT', 'TEXTAREA', 'CODE', 'PRE']);
const TRANSLATABLE_ATTRS = ['placeholder', 'title', 'aria-label', 'alt'];
const NUMERIC_OR_URL = /^(?:[\d\s.,:/\\()%+\-$€£¥₹]+|https?:\/\/\S+|[\w.+-]+@[\w-]+\.[\w.-]+)$/;
const NON_LATIN_LANGS = ['kn', 'ko', 'hi', 'ta', 'te', 'ml', 'bn'];
const COALESCE_MS = 250;
const CHUNK_SIZE = 300; // yield to the browser every N writes so clicks aren't starved
const WALK_CHUNK = 500; // yield during collectFrom every N nodes
const IDLE_TIMEOUT_MS = 2000; // max wait before forcing drain — input has priority inside this window

/**
 * Yield to the browser for one macrotask. Uses MessageChannel for a ~0ms
 * round-trip that still lets the browser paint and process user input,
 * falling back to setTimeout(0) elsewhere.
 */
const yieldToBrowser = (): Promise<void> =>
  new Promise((resolve) => {
    if (typeof MessageChannel === 'function') {
      const channel = new MessageChannel();
      channel.port1.onmessage = () => {
        channel.port1.close();
        channel.port2.close();
        resolve();
      };
      channel.port2.postMessage(null);
    } else {
      window.setTimeout(resolve, 0);
    }
  });
const CACHE_KEY = 'quikstep:translationCache:v1';
const CACHE_FLUSH_DEBOUNCE_MS = 800;
const IS_DEV = process.env.NODE_ENV !== 'production';
const LOG_PREFIX = '[translation]';

// ----------------------------- context shape -----------------------------

interface TranslationContextValue {
  isTranslating: boolean;
}

const TranslationContext = createContext<TranslationContextValue>({ isTranslating: false });

export const useTranslationStatus = () => useContext(TranslationContext);

// ----------------------------- cache helpers -----------------------------

type Cache = Record<string, string>; // `${lang}|${text}` -> translated

const loadCache = (): Cache => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const cacheKey = (lang: string, text: string) => `${lang}|${text}`;

// ----------------------------- text utilities -----------------------------

const isSkippableText = (text: string) => {
  const trimmed = text.trim();
  if (!trimmed) return true;
  // Pure whitespace/punctuation only
  if (!/[A-Za-z]/.test(trimmed)) return true;
  // Numbers / URLs / emails — don't send to Google, they'd mangle them
  return NUMERIC_OR_URL.test(trimmed);
};

const isInsideSkipTree = (node: Node): boolean => {
  let el: Element | null = node.nodeType === Node.TEXT_NODE ? (node.parentElement as Element | null) : (node as Element);
  while (el) {
    if (SKIP_ENTIRELY.has(el.tagName)) return true;
    if (SKIP_TEXT_KEEP_ATTRS.has(el.tagName)) return true;
    if (el.getAttribute && (el.getAttribute('data-no-translate') === 'true' || el.getAttribute('translate') === 'no')) {
      return true;
    }
    // contentEditable regions (TipTap, Draft.js, raw contentEditable). The user
    // is *authoring* text here — translating as they type would corrupt their
    // input. Covers both the attribute form and the IDL property.
    if (el.getAttribute && el.getAttribute('contenteditable') === 'true') return true;
    if ((el as HTMLElement).isContentEditable) return true;
    if (el.classList && el.classList.contains('notranslate')) return true;
    el = el.parentElement;
  }
  return false;
};

// -------------------- token protection (restricted words) --------------------

interface TokenPlan {
  processed: string;
  restore: Record<string, string>; // token -> replacement
}

const protectRestricted = (original: string, lang: string): TokenPlan => {
  let processed = original;
  const restore: Record<string, string> = {};
  restrictedWords.forEach((word: string, i: number) => {
    if (!processed.includes(word)) return;
    const token = `__RW_${i}__`;
    restore[token] = getOverride(word, lang) ?? word;
    processed = processed.split(word).join(token);
  });
  return { processed, restore };
};

const restoreTokens = async (translated: string, restore: Record<string, string>): Promise<string> => {
  let result = translated;
  for (const [token, replacement] of Object.entries(restore)) {
    result = result.split(token).join(replacement);
  }
  return result;
};

// -------------------- provider --------------------

interface Props {
  children: React.ReactNode;
  language: string;
}

export const TranslationProvider: React.FC<Props> = ({ children, language }) => {
  // Original text per node (text nodes only)
  const originalMap = useRef(new WeakMap<Node, string>());
  // Last language applied per node
  const appliedLang = useRef(new WeakMap<Node, string>());
  // Original attribute values — per-element map of attrName -> value
  const originalAttrs = useRef(new WeakMap<Element, Record<string, string>>());

  const cache = useRef<Cache>(loadCache());
  const flushTimer = useRef<number | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const pendingRef = useRef<Set<Node>>(new Set());
  const coalesceTimer = useRef<number | null>(null);
  // Separate handle for requestIdleCallback — different cancel API than setTimeout.
  const idleHandle = useRef<number | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);

  // Forward refs to the latest drainPending / scheduleDrain closures. Declared
  // up top so earlier callbacks (e.g. drainPending's own finally) can invoke
  // the CURRENT-language closures without stale-closure bugs across language
  // switches.
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const drainPendingRef = useRef<(() => Promise<void>) | null>(null);
  const scheduleDrainRef = useRef<(() => void) | null>(null);

  const [isTranslating, setIsTranslating] = useState(false);

  // --------- cache flush ---------

  const flushCache = useCallback(() => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache.current));
    } catch {
      // localStorage full or unavailable — drop silently; cache still works in-memory
    }
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimer.current) window.clearTimeout(flushTimer.current);
    flushTimer.current = window.setTimeout(flushCache, CACHE_FLUSH_DEBOUNCE_MS);
  }, [flushCache]);

  // --------- core translation ---------

  /**
   * Translate a list of original texts to `lang`, using cache + overrides +
   * token protection. Returns array aligned with input.
   */
  const translateTexts = useCallback(
    async (texts: string[], lang: string): Promise<string[]> => {
      if (!texts.length) return [];

      // Build per-index plans and figure out which texts need a network call.
      const plans: (TokenPlan & { original: string; override: string | null })[] = texts.map((t) => {
        const override = getOverride(t.trim(), lang);
        if (override) return { processed: '', restore: {}, original: t, override };
        return { ...protectRestricted(t, lang), original: t, override: null };
      });

      // Gather the processed forms that actually need Google.
      const needNetwork: { idx: number; text: string }[] = [];
      plans.forEach((p, idx) => {
        if (p.override !== null) return;
        const key = cacheKey(lang, p.processed);
        if (cache.current[key] != null) return;
        needNetwork.push({ idx, text: p.processed });
      });

      if (needNetwork.length > 0) {
        const googleResults = await translateBatch(
          needNetwork.map((n) => n.text),
          lang
        );
        // Merge into cache
        let unchanged = 0;
        needNetwork.forEach((n, i) => {
          const translated = googleResults[i] ?? n.text;
          if (translated === n.text) unchanged++;
          cache.current[cacheKey(lang, n.text)] = translated;
        });
        scheduleFlush();
        if (IS_DEV) {
          // eslint-disable-next-line no-console
          console.log(
            `${LOG_PREFIX} translated ${needNetwork.length} items -> ${lang}` +
              ` (cache hits: ${texts.length - needNetwork.length}, unchanged-by-api: ${unchanged})` +
              (unchanged === needNetwork.length && lang !== 'en'
                ? ' — ALL RETURNED UNCHANGED. Likely a network/CORS failure on translate.googleapis.com.'
                : '')
          );
        }
      } else if (IS_DEV && texts.length > 0) {
        // eslint-disable-next-line no-console
        console.log(`${LOG_PREFIX} ${texts.length} items served from cache -> ${lang}`);
      }

      // Final resolution per index
      return Promise.all(
        plans.map(async (p) => {
          if (p.override !== null) return p.override;
          const cached = cache.current[cacheKey(lang, p.processed)];
          let result = cached ?? p.processed;
          result = await restoreTokens(result, p.restore);

          // Transliterate Latin-only text if Google returned the original unchanged
          // (happens for proper nouns + non-Latin target langs).
          const isLatinScript = /^[a-zA-Z0-9\s.,!?'-]+$/.test(p.original);
          if (NON_LATIN_LANGS.includes(lang) && isLatinScript && result.trim().toLowerCase() === p.original.trim().toLowerCase()) {
            const words = p.original.split(' ');
            const transliterated = await Promise.all(words.map((w) => transliterate(w, lang)));
            result = transliterated.join(' ');
          }

          return result;
        })
      );
    },
    [scheduleFlush]
  );

  // --------- node walking ---------

  /**
   * Walk `root`'s subtree and collect translation candidates. Iterative (no
   * stack growth on deep trees) and async — yields to the browser every
   * WALK_CHUNK nodes so clicks, scroll, and paint can fire between chunks.
   */
  const collectFrom = useCallback(async (root: Node, texts: Node[], elementsForAttrs: Element[], lang: string): Promise<void> => {
    const stack: Node[] = [root];
    let processed = 0;

    while (stack.length > 0) {
      const node = stack.pop()!;

      if (node.nodeType === Node.TEXT_NODE) {
        // Already at target language — skip.
        if (appliedLang.current.get(node) !== lang) {
          if (!isInsideSkipTree(node)) {
            const current = originalMap.current.get(node) ?? node.nodeValue ?? '';
            if (!isSkippableText(current)) texts.push(node);
          }
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        let blockedByAncestor = false;
        if (isInsideSkipTree(el)) {
          blockedByAncestor = el.parentElement !== null && isInsideSkipTree(el.parentElement);
        }

        if (!blockedByAncestor && !SKIP_ENTIRELY.has(el.tagName)) {
          // Attribute translation works even for SKIP_TEXT_KEEP_ATTRS elements
          // (we want <input placeholder> translated while leaving its value alone).
          for (const attr of TRANSLATABLE_ATTRS) {
            if (el.hasAttribute(attr)) {
              const value = el.getAttribute(attr);
              if (value && !isSkippableText(value)) {
                elementsForAttrs.push(el);
                break;
              }
            }
          }

          // Recurse into children only if this element holds UI text.
          if (!SKIP_TEXT_KEEP_ATTRS.has(el.tagName)) {
            const children = el.childNodes;
            // Push in reverse to preserve DFS order when popping.
            for (let i = children.length - 1; i >= 0; i--) {
              stack.push(children[i]);
            }
          }
        }
      }

      processed++;
      if (processed >= WALK_CHUNK) {
        // Yield to the browser — lets queued clicks, scroll, and paint
        // run before we continue walking.
        await yieldToBrowser();
        processed = 0;
      }
    }
  }, []);

  // --------- apply translations ---------

  const process = useCallback(
    async (roots: Node[]) => {
      if (!roots.length) return;

      const textNodes: Node[] = [];
      const elementsForAttrs: Element[] = [];
      for (const root of roots) {
        // Each root's walk yields to the browser internally. Sequential so
        // we don't fan out huge amounts of work at once, but the yields
        // interleave with browser frames regardless.
        await collectFrom(root, textNodes, elementsForAttrs, language);
      }

      // --- text nodes ---
      const textNodesToTranslate: Node[] = [];
      const originals: string[] = [];
      for (const node of textNodes) {
        const already = appliedLang.current.get(node);
        if (already === language) continue;
        if (!originalMap.current.has(node)) {
          originalMap.current.set(node, node.nodeValue || '');
        }
        const original = originalMap.current.get(node) || '';
        // Fast path: en resets to original
        if (language === 'en') {
          if (node.nodeValue !== original) node.nodeValue = original;
          appliedLang.current.set(node, 'en');
          continue;
        }
        textNodesToTranslate.push(node);
        originals.push(original);
      }

      // --- attributes ---
      interface AttrJob {
        el: Element;
        attr: string;
        original: string;
      }
      const attrJobs: AttrJob[] = [];
      for (const el of elementsForAttrs) {
        let map = originalAttrs.current.get(el);
        if (!map) {
          map = {};
          originalAttrs.current.set(el, map);
        }
        for (const attr of TRANSLATABLE_ATTRS) {
          if (!el.hasAttribute(attr)) continue;
          const currentValue = el.getAttribute(attr) || '';
          if (isSkippableText(currentValue)) continue;
          if (!(attr in map)) map[attr] = currentValue;
          const original = map[attr];
          if (language === 'en') {
            if (el.getAttribute(attr) !== original) el.setAttribute(attr, original);
            continue;
          }
          attrJobs.push({ el, attr, original });
        }
      }

      if (!textNodesToTranslate.length && !attrJobs.length) return;

      const allOriginals = [...originals, ...attrJobs.map((j) => j.original)];
      const translated = await translateTexts(allOriginals, language);

      // Disconnect observer while we write, to avoid self-triggering.
      // For large batches, yield to the browser between chunks so clicks
      // and scroll don't starve while we paint.
      const reconnect = () =>
        observerRef.current?.observe(document.body, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: TRANSLATABLE_ATTRS
        });

      observerRef.current?.disconnect();
      try {
        for (let i = 0; i < textNodesToTranslate.length; i++) {
          const node = textNodesToTranslate[i];
          const result = translated[i];
          if (result != null && node.nodeValue !== result) {
            node.nodeValue = result;
          }
          appliedLang.current.set(node, language);
          if (i > 0 && i % CHUNK_SIZE === 0) {
            // Reconnect briefly so mutations during yield aren't lost, then
            // yield one frame to let the browser paint and handle input.
            reconnect();
            await yieldToBrowser();
            observerRef.current?.disconnect();
          }
        }
        for (let i = 0; i < attrJobs.length; i++) {
          const { el, attr } = attrJobs[i];
          const result = translated[originals.length + i];
          if (result != null && el.getAttribute(attr) !== result) {
            el.setAttribute(attr, result);
          }
          if (i > 0 && i % CHUNK_SIZE === 0) {
            reconnect();
            await yieldToBrowser();
            observerRef.current?.disconnect();
          }
        }
      } finally {
        reconnect();
      }
    },
    [collectFrom, language, translateTexts]
  );

  // --------- coalesced processing ---------

  const drainPending = useCallback(async () => {
    coalesceTimer.current = null;
    if (!pendingRef.current.size) return;
    let roots: Node[] = Array.from(pendingRef.current);
    pendingRef.current.clear();

    // Dedupe: if the whole body was scheduled (language switch, or lots of
    // overlapping subtrees) we don't need to also walk every descendant
    // that got queued. One tree walk is far cheaper than N overlapping ones.
    if (roots.includes(document.body) || roots.length > 50) {
      roots = [document.body];
    } else {
      // Drop descendants whose ancestor is also in the set.
      const rootSet = new Set(roots);
      roots = roots.filter((n) => {
        let p = n.parentNode;
        while (p) {
          if (rootSet.has(p)) return false;
          p = p.parentNode;
        }
        return true;
      });
    }

    // Wait for prior run (back-pressure). Best-effort — if the prior run
    // was for a different language it may overwrite between phases, but
    // the new pass below will correct that.
    if (inFlight.current) {
      try {
        await inFlight.current;
      } catch {
        /* ignore */
      }
    }

    setIsTranslating(true);
    const run = process(roots).finally(() => {
      setIsTranslating(false);
      // Only clear inFlight if WE are the active run. A newer drain may
      // have already replaced it; don't trample that.
      if (inFlight.current === run) inFlight.current = null;
      // If new mutations piled up during the run, queue another drain via
      // the idle-aware scheduler.
      if (pendingRef.current.size && coalesceTimer.current == null && idleHandle.current == null) {
        scheduleDrainRef.current?.();
      }
    });
    inFlight.current = run;
    await run;
  }, [process]);

  // Cancels both the setTimeout and requestIdleCallback variants, whichever
  // was used.
  const cancelDrain = useCallback(() => {
    if (coalesceTimer.current != null) {
      window.clearTimeout(coalesceTimer.current);
      coalesceTimer.current = null;
    }
    if (idleHandle.current != null) {
      const w = window as typeof window & {
        cancelIdleCallback?: (h: number) => void;
      };
      w.cancelIdleCallback?.(idleHandle.current);
      idleHandle.current = null;
    }
  }, []);

  // Schedule `drainPending` preferring requestIdleCallback so we wait out
  // React's commit phase instead of piling tree-walk work on top of it. The
  // `timeout` guarantees we still fire within a bounded window on pages that
  // never idle (heavy animations, constant polling). Falls back to setTimeout
  // on Safari.
  const scheduleDrain = useCallback(() => {
    const w = window as typeof window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
    };
    if (typeof w.requestIdleCallback === 'function') {
      idleHandle.current = w.requestIdleCallback(
        () => {
          idleHandle.current = null;
          drainPendingRef.current?.();
        },
        { timeout: IDLE_TIMEOUT_MS }
      );
    } else {
      coalesceTimer.current = window.setTimeout(() => {
        coalesceTimer.current = null;
        drainPendingRef.current?.();
      }, COALESCE_MS);
    }
  }, []);

  // Debounced: every new mutation extends the wait. During a route change's
  // mutation burst we only drain once React goes quiet — no tree walks on
  // top of React's commit phase.
  const schedule = useCallback(
    (root: Node) => {
      pendingRef.current.add(root);
      cancelDrain();
      scheduleDrain();
    },
    [cancelDrain, scheduleDrain]
  );

  // Keep forward-refs pointing at the latest closures so callbacks that ran
  // under the previous language (drainPending.finally, MutationObserver) can
  // invoke the CURRENT-language versions.
  useEffect(() => {
    drainPendingRef.current = drainPending;
  }, [drainPending]);
  useEffect(() => {
    scheduleDrainRef.current = scheduleDrain;
  }, [scheduleDrain]);

  // --------- mount / language change ---------

  useEffect(() => {
    if (IS_DEV) {
      // eslint-disable-next-line no-console
      console.log(`${LOG_PREFIX} language set to "${language}" — scheduling full-body pass`);
    }
    // Snapshot ref targets for use in cleanup (satisfies react-hooks rule).
    const pending = pendingRef.current;
    // On every language change (including mount), schedule a full-body pass.
    schedule(document.body);

    observerRef.current = new MutationObserver((mutations) => {
      // Hot path: when React reconciles a subtree back to its English source
      // and we already have the translation cached, apply it synchronously
      // right here. No drain, no 250ms wait, no full-tree walk. This keeps
      // the main thread free during heavy re-render cycles.
      const observer = observerRef.current;
      if (!observer) return;
      observer.disconnect();
      try {
        for (const m of mutations) {
          if (m.type === 'childList') {
            m.addedNodes.forEach((n) => schedule(n));
          } else if (m.type === 'characterData') {
            const target = m.target;
            const newValue = target.nodeValue ?? '';
            const storedOriginal = originalMap.current.get(target);

            if (storedOriginal !== undefined && newValue === storedOriginal) {
              // React-reset: target just snapped back to the English source.
              if (language === 'en') {
                appliedLang.current.set(target, 'en');
                continue;
              }
              const cached = cache.current[cacheKey(language, storedOriginal)];
              if (cached != null && cached !== storedOriginal) {
                target.nodeValue = cached;
                appliedLang.current.set(target, language);
                continue;
              }
              // Cache miss (shouldn't normally happen after the first pass) —
              // fall back to the async drain.
              appliedLang.current.delete(target);
              schedule(target);
            } else if (storedOriginal !== undefined && newValue !== storedOriginal) {
              // Either this is our own write (observer was disconnected, so
              // shouldn't fire) or a genuine content change. If nothing was
              // ever applied, treat as fresh content.
              if (!appliedLang.current.get(target)) {
                originalMap.current.delete(target);
                appliedLang.current.delete(target);
                schedule(target);
              }
            } else {
              schedule(target);
            }
          } else if (m.type === 'attributes' && m.target.nodeType === Node.ELEMENT_NODE) {
            const el = m.target as Element;
            const attr = m.attributeName!;
            if (!TRANSLATABLE_ATTRS.includes(attr)) continue;
            const currentValue = el.getAttribute(attr) ?? '';
            const attrMap = originalAttrs.current.get(el);
            const storedOriginal = attrMap?.[attr];
            if (storedOriginal !== undefined && currentValue === storedOriginal && language !== 'en') {
              const cached = cache.current[cacheKey(language, storedOriginal)];
              if (cached != null && cached !== storedOriginal) {
                el.setAttribute(attr, cached);
                continue;
              }
            }
            schedule(el);
          }
        }
      } finally {
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: TRANSLATABLE_ATTRS
        });
      }
    });
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: TRANSLATABLE_ATTRS
    });

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      // Cancels both setTimeout and requestIdleCallback variants.
      cancelDrain();
      if (flushTimer.current !== null) {
        window.clearTimeout(flushTimer.current);
        flushTimer.current = null;
        flushCache();
      }
      // Drop any pending roots queued under the old language — the new
      // effect will re-schedule a full-body pass with the new language.
      pending.clear();
      // Also discard any in-flight promise reference. The underlying
      // translation call can't be cancelled, but we don't want the new
      // drain to back-pressure off it (that promise writes the OLD
      // language, which our new pass will overwrite anyway).
      inFlight.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const value = useMemo(() => ({ isTranslating }), [isTranslating]);

  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
};
